import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faCircleXmark, faRunning, faWater,
    faSpinner, faExclamationTriangle, faChevronLeft, faChevronRight,
    faBook, faAppleAlt, faBed, faCheckSquare
} from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import './AppProfileModal.css';
import './NutritionDailyView.css';
import './HabitTracker.css';
import './AppUserDetails.css';
import HeartFillComponent from './HeartFillComponent';
import NutritionDailyView from './NutritionDailyView';
import AppUserDetails from './AppUserDetails';
import Lottie from 'react-lottie-player';
import PatientLongevityAI from './PatientLongevityAI/PatientLongevityAI';

const formatShortDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "N/A";
const formatTime = d => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A";
const calculateDuration = (start, end) => {
    if (!start || !end) return { hours: 0, minutes: 0, text: 'N/A', totalHours: 0 };
    const diff = new Date(end) - new Date(start);
    if (isNaN(diff) || diff < 0) return { hours: 0, minutes: 0, text: 'Error', totalHours: 0 };
    const totalMinutes = diff / (1000 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return { hours, minutes, text: `${hours}h ${minutes}m`, totalHours: totalMinutes / 60 };
};
const getLatestNRecordsAsc = (dataArray = [], dateKey = 'date', n = 10) =>
    dataArray?.length
        ? [...dataArray].sort((a, b) => new Date(b[dateKey]) - new Date(a[dateKey])).slice(0, n).reverse()
        : [];

const TEMP_PLATFORM_VIEW_URL = 'https://service3.szapfs.org/temp-platform-view';

const RANGE_PRESETS = [
    { key: '24h', label: '24H' },
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' }
];

const NUTRIENT_NAME_BY_ID = {
    203: 'Protein',
    204: 'Total lipid (fat)',
    205: 'Carbohydrate, by difference',
    208: 'Energy',
    291: 'Fiber, total dietary',
    301: 'Calcium, Ca',
    303: 'Iron, Fe',
    304: 'Magnesium, Mg',
    306: 'Potassium, K',
    320: 'Vitamin A, RAE',
    328: 'Vitamin D (D2 + D3)',
    401: 'Vitamin C, total ascorbic acid'
};

const formatDateInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getPresetDateRange = (presetKey) => {
    const end = new Date();
    const start = new Date(end);

    if (presetKey === '30d') {
        start.setDate(end.getDate() - 29);
    } else {
        start.setDate(end.getDate() - 6);
    }

    return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end)
    };
};

const getRangeQuery = (presetKey, startDate, endDate) => {
    if (presetKey === '24h') {
        return {
            startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            endTime: new Date().toISOString()
        };
    }

    if (!startDate || !endDate) {
        return { startTime: '', endTime: '' };
    }

    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return { startTime: '', endTime: '' };
    }

    return {
        startTime: start.toISOString(),
        endTime: end.toISOString()
    };
};

const toISOFromMs = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && Number.isNaN(Number(value))) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    const date = new Date(numeric < 1000000000000 ? numeric * 1000 : numeric);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const dateKeyFromTime = (value) => {
    const iso = toISOFromMs(value);
    return iso ? iso.split('T')[0] : null;
};

const normalizeDurationMinutes = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return numeric <= 24 ? numeric * 60 : numeric;
};

const normalizeNutrientName = (nutrient = {}) => {
    const id = Number(nutrient.id);
    if (NUTRIENT_NAME_BY_ID[id]) return NUTRIENT_NAME_BY_ID[id];

    const rawName = (nutrient.name || nutrient.nutrient || '').toLowerCase();
    if (rawName.includes('energy') || rawName.includes('calorie')) return 'Energy';
    if (rawName.includes('protein')) return 'Protein';
    if (rawName.includes('carbohydrate') || rawName === 'carbs') return 'Carbohydrate, by difference';
    if (rawName.includes('lipid') || rawName === 'fat' || rawName.includes('total fat')) return 'Total lipid (fat)';
    if (rawName.includes('fiber')) return 'Fiber, total dietary';
    if (rawName.includes('sugar')) return 'Sugars, Total';
    if (rawName.includes('sodium')) return 'Sodium, Na';
    if (rawName.includes('calcium')) return 'Calcium, Ca';
    if (rawName.includes('iron')) return 'Iron, Fe';
    if (rawName.includes('potassium')) return 'Potassium, K';
    return nutrient.name || nutrient.nutrient || 'Nutrient';
};

const normalizeNutrient = (nutrient = {}) => ({
    id: nutrient.id,
    nutrient: normalizeNutrientName(nutrient),
    name: nutrient.name || nutrient.nutrient,
    amount: Number(nutrient.amount) || 0,
    unit: nutrient.unit_name || nutrient.unit
});

const buildUserDetails = (appUser = {}) => {
    const availableReports = appUser.available_reports || appUser.availableReports;
    const patientMetadata = appUser.patientMetadata || appUser.patient_metadata || (
        availableReports ? [{ available_reports: availableReports }] : []
    );

    return {
        ...appUser,
        First_Name: appUser.First_Name || appUser.first_name || appUser.firstName,
        Last_Name: appUser.Last_Name || appUser.last_name || appUser.lastName,
        Email: appUser.Email || appUser.email,
        Sex: appUser.Sex || appUser.gender || appUser.Gender,
        Age: appUser.Age || appUser.age,
        Height: appUser.Height || appUser.height_cm || appUser.Height_cm,
        Weight: appUser.Weight || appUser.weight_kg || appUser.Weight_kg,
        Country: appUser.Country || appUser.country,
        PatientID: appUser.PatientID || appUser.patient_id || appUser.patientId,
        identifier: appUser.identifier,
        patientMetadata
    };
};

const aggregateDailyMetric = (items = [], valueKey, outputKey) => {
    const byDate = {};

    items.forEach(item => {
        const date = dateKeyFromTime(item.start_time_ms || item.timestamp || item.date);
        const value = Number(item[valueKey]);
        if (!date || !Number.isFinite(value)) return;
        byDate[date] = (byDate[date] || 0) + value;
    });

    return Object.entries(byDate)
        .map(([date, value]) => ({ date, [outputKey]: value }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
};

const normalizeMeals = (meals = []) => meals.map(meal => {
    const timeDate = typeof meal.time === 'string' ? meal.time.split('T')[0] : null;
    const date = meal.date || timeDate || dateKeyFromTime(meal.time);
    const time = meal.time || (date ? `${date}T12:00:00` : null);

    return {
        id: meal.id,
        type: meal.meal_type || meal.type || 'Meal',
        time,
        date,
        items: (meal.foods || meal.items || []).map(food => ({
            id: food.meal_item_id || food.id,
            description: food.food_name || food.description || food.name || 'Food',
            amount: food.amount,
            nutrients: (food.nutrients || []).map(normalizeNutrient)
        }))
    };
});

const buildMacroSummary = (meals = []) => {
    const byDate = {};

    meals.forEach(meal => {
        const date = meal.date || meal.time?.split('T')[0];
        if (!date) return;
        if (!byDate[date]) byDate[date] = {};

        meal.items?.forEach(item => {
            item.nutrients?.forEach(nutrient => {
                const key = nutrient.nutrient;
                if (!byDate[date][key]) {
                    byDate[date][key] = { ...nutrient, amount: 0 };
                }
                byDate[date][key].amount += Number(nutrient.amount) || 0;
            });
        });
    });

    return {
        dailySummary: Object.entries(byDate)
            .map(([date, nutrientsByName]) => ({
                date,
                nutrients: Object.values(nutrientsByName)
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date))
    };
};

const normalizeSleep = (data = {}, meta = {}) => {
    const sleepSummary = data.sleepSummary || {};
    const series = sleepSummary.series || {};
    const consistencyByDate = {};

    (series.consistency || []).forEach(item => {
        const date = dateKeyFromTime(item.bed_time_ms || item.wake_time_ms || item.date_ms);
        if (date) consistencyByDate[date] = item;
    });

    const durationSeries = series.duration || [];
    const sleep = durationSeries.map(item => {
        const date = dateKeyFromTime(item.date_ms);
        const consistency = consistencyByDate[date] || {};
        const sleepTime = toISOFromMs(consistency.bed_time_ms);
        const wakeTime = toISOFromMs(consistency.wake_time_ms);

        return {
            date,
            sleepTime,
            wakeTime,
            durationMinutes: normalizeDurationMinutes(item.value)
        };
    }).filter(item => item.date && item.durationMinutes);

    if (!sleep.length && data.lastSession?.duration_min) {
        const endDate = meta.endDate || dateKeyFromTime(meta.endMs) || formatDateInput(new Date());
        sleep.push({
            date: endDate,
            sleepTime: null,
            wakeTime: null,
            durationMinutes: Number(data.lastSession.duration_min)
        });
    }

    return sleep.sort((a, b) => new Date(a.date) - new Date(b.date));
};

const arrayFromPayload = (value, nestedKey) => {
    if (Array.isArray(value)) return value;
    if (nestedKey && Array.isArray(value?.[nestedKey])) return value[nestedKey];
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.rows)) return value.rows;
    if (Array.isArray(value?.results)) return value.results;
    if (Array.isArray(value?.records)) return value.records;
    if (nestedKey && value?.[nestedKey] && typeof value[nestedKey] === 'object') {
        return arrayFromPayload(value[nestedKey], nestedKey);
    }
    if (value?.data && typeof value.data === 'object') return arrayFromPayload(value.data, nestedKey);
    return [];
};

const pickFirst = (source = {}, keys = []) => {
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
};

const normalizeGoalTime = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'string') return value;
    return String(value);
};

const normalizeBoolean = (value, fallback = true) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'inactive'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'active'].includes(normalized)) return true;
    }
    return fallback;
};

const normalizeCompletionList = (habit = {}) => {
    const rawCompletions = pickFirst(habit, [
        'completions',
        'completedDates',
        'completed_dates',
        'completionDates',
        'completion_dates',
        'completedHabitLogs',
        'completed_habit_logs',
        'logs'
    ]);

    if (!Array.isArray(rawCompletions) || !rawCompletions.length) return undefined;

    const completions = rawCompletions
        .map(item => {
            if (typeof item === 'string') return item;
            return pickFirst(item, ['timestamp', 'date', 'completed_at', 'completedAt', 'created_at', 'createdAt']);
        })
        .filter(Boolean);

    return completions.length ? completions : undefined;
};

const getLocalDateKey = (date) => formatDateInput(date);

const dateKeyFromUnknown = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
        const match = value.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) return match[0];
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return getLocalDateKey(parsed);
};

const isHabitActiveOnDate = (habit, date) => {
    if (habit.isActive === false) return false;

    const selectedKey = getLocalDateKey(date);
    const startKey = dateKeyFromUnknown(habit.dateAdded);
    if (startKey && startKey > selectedKey) return false;

    const todayKey = getLocalDateKey(new Date());
    if (selectedKey > todayKey) return false;

    return true;
};

const completionMatchesDate = (completion, dateKey) => dateKeyFromUnknown(completion) === dateKey;

const HABIT_ID_KEYS = ['habitId', 'habit_id', 'Habit_ID', 'habitID', 'definitionId', 'definition_id', 'id'];
const HABIT_COMPLETED_KEYS = ['completed', 'isCompleted', 'completedToday', 'completed_today', 'done', 'checked', 'is_complete', 'isComplete'];
const HABIT_COMPLETION_WRAPPER_KEYS = ['data', 'items', 'rows', 'records', 'results', 'habits', 'completions', 'logs'];

const normalizeHabitId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
};

const isCompletionValueTruthy = (value) => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return false;
        if (['false', '0', 'no', 'missed', 'incomplete', 'pending'].includes(normalized)) return false;
        return true;
    }
    return true;
};

const getHabitIdFromRecord = (record = {}) => normalizeHabitId(pickFirst(record, HABIT_ID_KEYS));

const normalizeHabitDefinition = (habit = {}, index = 0) => {
    const description = pickFirst(habit, [
        'description',
        'title',
        'name',
        'habit',
        'habit_name',
        'habitName'
    ]) || 'Habit';

    return {
        ...habit,
        id: getHabitIdFromRecord(habit) ?? `habit-${index}`,
        userId: pickFirst(habit, ['userId', 'user_id', 'User_ID']),
        dateAdded: pickFirst(habit, ['dateAdded', 'date_added', 'createdAt', 'created_at', 'timestamp']),
        goalTime: normalizeGoalTime(pickFirst(habit, ['goalTime', 'goal_time', 'targetTime', 'target_time', 'reminderTime', 'reminder_time'])),
        isActive: normalizeBoolean(pickFirst(habit, ['isActive', 'is_active', 'active']), true),
        description,
        notes: habit.notes,
        completions: normalizeCompletionList(habit)
    };
};

const mergeHabitDefinitions = (base, next) => ({
    ...base,
    ...next,
    id: base.id ?? next.id,
    description: base.description && base.description !== 'Habit' ? base.description : next.description,
    dateAdded: base.dateAdded || next.dateAdded,
    goalTime: base.goalTime || next.goalTime,
    isActive: base.isActive ?? next.isActive,
    notes: base.notes || next.notes,
    completions: base.completions || next.completions
});

const addCompletion = (completionMap, habitId, dateKey) => {
    const normalizedId = normalizeHabitId(habitId);
    if (!normalizedId || !dateKey) return;
    const existing = completionMap.get(normalizedId) || new Set();
    existing.add(dateKey);
    completionMap.set(normalizedId, existing);
};

const normalizeDateKeyedCompletionEntry = (completionMap, dateKey, entry, keyHint) => {
    if (!entry) return;

    if (Array.isArray(entry)) {
        entry.forEach(item => normalizeDateKeyedCompletionEntry(completionMap, dateKey, item));
        return;
    }

    if (typeof entry !== 'object') {
        if (isCompletionValueTruthy(entry)) addCompletion(completionMap, keyHint ?? entry, dateKey);
        return;
    }

    const recordHabitId = getHabitIdFromRecord(entry) ?? normalizeHabitId(keyHint);
    const completedFlag = pickFirst(entry, HABIT_COMPLETED_KEYS);

    if (recordHabitId) {
        if (isCompletionValueTruthy(completedFlag)) addCompletion(completionMap, recordHabitId, dateKey);
        return;
    }

    Object.entries(entry).forEach(([habitId, value]) => {
        if (HABIT_COMPLETION_WRAPPER_KEYS.includes(habitId) && value && typeof value === 'object') {
            normalizeDateKeyedCompletionEntry(completionMap, dateKey, value);
            return;
        }

        if (Array.isArray(value)) {
            value.forEach(item => normalizeDateKeyedCompletionEntry(completionMap, dateKey, item, habitId));
            return;
        }

        if (value && typeof value === 'object') {
            const nestedHabitId = getHabitIdFromRecord(value) ?? normalizeHabitId(habitId);
            if (isCompletionValueTruthy(pickFirst(value, HABIT_COMPLETED_KEYS))) addCompletion(completionMap, nestedHabitId, dateKey);
            return;
        }

        if (isCompletionValueTruthy(typeof value === 'object' ? pickFirst(value, HABIT_COMPLETED_KEYS) : value)) {
            addCompletion(completionMap, habitId, dateKey);
        }
    });
};

const normalizeHabitsCompletions = (completionPayload) => {
    const completionMap = new Map();
    if (!completionPayload) return completionMap;

    const completionRows = arrayFromPayload(completionPayload, 'habitsCompletions');
    if (completionRows.length) {
        completionRows.forEach(row => {
            if (!row || typeof row !== 'object') return;
            const dateKey = dateKeyFromUnknown(pickFirst(row, ['date', 'completed_at', 'completedAt', 'timestamp', 'created_at', 'createdAt']));
            const habitId = getHabitIdFromRecord(row);
            const completedFlag = pickFirst(row, HABIT_COMPLETED_KEYS);
            if (dateKey && habitId && isCompletionValueTruthy(completedFlag)) addCompletion(completionMap, habitId, dateKey);
        });
        return completionMap;
    }

    const keyedCompletions = completionPayload?.history && !Array.isArray(completionPayload.history)
        ? completionPayload.history
        : completionPayload?.habitsCompletions && !Array.isArray(completionPayload.habitsCompletions)
        ? completionPayload.habitsCompletions
        : completionPayload?.data && !Array.isArray(completionPayload.data)
            ? completionPayload.data
            : completionPayload;

    Object.entries(keyedCompletions || {}).forEach(([rawDateKey, entry]) => {
        const dateKey = dateKeyFromUnknown(rawDateKey);
        if (!dateKey) return;
        normalizeDateKeyedCompletionEntry(completionMap, dateKey, entry);
    });

    return completionMap;
};

const normalizeHabitCompletionHistory = (completionPayload) => {
    const history = {};
    if (!completionPayload) return history;

    const keyedHistory = completionPayload?.history && !Array.isArray(completionPayload.history)
        ? completionPayload.history
        : completionPayload?.data?.history && !Array.isArray(completionPayload.data.history)
            ? completionPayload.data.history
            : completionPayload;

    Object.entries(keyedHistory || {}).forEach(([rawDateKey, entry]) => {
        const dateKey = dateKeyFromUnknown(rawDateKey);
        if (!dateKey) return;

        const entries = Array.isArray(entry) ? entry : Object.entries(entry || {}).map(([habitId, value]) => (
            value && typeof value === 'object'
                ? { ...value, habitId: getHabitIdFromRecord(value) ?? habitId }
                : { habitId, completed: value }
        ));

        history[dateKey] = entries
            .filter(item => isCompletionValueTruthy(pickFirst(item, HABIT_COMPLETED_KEYS)))
            .map(item => ({
                ...item,
                id: getHabitIdFromRecord(item),
                habitId: getHabitIdFromRecord(item),
                completed: true
            }))
            .filter(item => item.habitId);
    });

    return history;
};

const collectHabitRecordsFromCompletions = (completionPayload) => {
    const recordsById = new Map();
    if (!completionPayload) return [];

    const rememberRecord = (record) => {
        if (!record || typeof record !== 'object') return;
        const habitId = getHabitIdFromRecord(record);
        if (!habitId) return;
        recordsById.set(habitId, recordsById.has(habitId)
            ? mergeHabitDefinitions(recordsById.get(habitId), normalizeHabitDefinition(record, recordsById.size))
            : normalizeHabitDefinition(record, recordsById.size));
    };

    const scanEntry = (entry) => {
        if (!entry) return;
        if (Array.isArray(entry)) {
            entry.forEach(scanEntry);
            return;
        }
        if (typeof entry !== 'object') return;

        const directHabitId = getHabitIdFromRecord(entry);
        if (directHabitId) {
            rememberRecord(entry);
            return;
        }

        Object.entries(entry).forEach(([key, value]) => {
            if (Array.isArray(value) || (value && typeof value === 'object')) {
                scanEntry(value);
                return;
            }

            const habitId = normalizeHabitId(key);
            if (habitId && isCompletionValueTruthy(value)) {
                rememberRecord({ id: habitId });
            }
        });
    };

    const history = completionPayload.history && !Array.isArray(completionPayload.history)
        ? completionPayload.history
        : completionPayload;

    Object.values(history || {}).forEach(scanEntry);
    return Array.from(recordsById.values());
};

const normalizeHabits = (habitPayload, habitsCompletionsPayload) => {
    const habitsById = new Map();

    const addHabit = (habit, index) => {
        if (!habit) return;
        const normalized = normalizeHabitDefinition(habit, index);
        const key = normalizeHabitId(normalized.id);
        if (!key) return;
        habitsById.set(key, habitsById.has(key)
            ? mergeHabitDefinitions(habitsById.get(key), normalized)
            : normalized);
    };

    arrayFromPayload(habitPayload, 'habits').forEach(addHabit);
    collectHabitRecordsFromCompletions(habitsCompletionsPayload).forEach(addHabit);

    const completionMap = normalizeHabitsCompletions(habitsCompletionsPayload);

    return Array.from(habitsById.values()).map(habit => {
        const explicitCompletions = habit.completions || [];
        const completionDates = completionMap.has(normalizeHabitId(habit.id))
            ? Array.from(completionMap.get(normalizeHabitId(habit.id)))
            : [];
        const mergedCompletions = Array.from(new Set([...explicitCompletions, ...completionDates]))
            .sort((a, b) => new Date(a) - new Date(b));

        return {
            ...habit,
            completions: habitsCompletionsPayload || mergedCompletions.length ? mergedCompletions : habit.completions
        };
    });
};

const normalizeHealthMetrics = (data = {}) => ({
    restingHeartRate: (data.restingHeartRate || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        bpm: Number(item.bpm)
    })).filter(item => item.timestamp && Number.isFinite(item.bpm)),
    hrv: (data.hrv || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        hrvMs: Number(item.hrv_ms),
        method: item.method
    })).filter(item => item.timestamp && Number.isFinite(item.hrvMs)),
    distance: (data.distance || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        meters: Number(item.meters)
    })).filter(item => item.timestamp && Number.isFinite(item.meters)),
    exercise: (data.exercise || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        durationMin: Number(item.duration_min),
        typeId: item.type_id,
        typeName: item.type_name || 'Exercise',
        caloriesKcal: Number(item.calories_kcal),
        distanceMeters: Number(item.distance_meters),
        sourceName: item.source_name
    })).filter(item => item.timestamp),
    calories: (data.calories || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        kind: item.kind || 'calories',
        kcal: Number(item.kcal)
    })).filter(item => item.timestamp && Number.isFinite(item.kcal)),
    bloodPressure: (data.bloodPressure || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        systolic: Number(item.systolic_mmhg),
        diastolic: Number(item.diastolic_mmhg),
        bodyPosition: item.body_position,
        measurementLocation: item.measurement_location
    })).filter(item => Number.isFinite(item.systolic) || Number.isFinite(item.diastolic)),
    respiratoryRate: (data.respiratoryRate || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        breathsPerMin: Number(item.breaths_per_min)
    })).filter(item => Number.isFinite(item.breathsPerMin)),
    bloodOxygen: (data.bloodOxygen || []).map(item => ({
        timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
        percent: Number(item.percent)
    })).filter(item => Number.isFinite(item.percent))
});

const normalizeTempPlatformViewResponse = (payload, appUser = {}, sourceRequestKey = null) => {
    const query = payload?.health?.query || {};
    const data = query.data || {};
    const nutrition = payload?.nutrition || {};
    const meals = normalizeMeals(nutrition.meals || []);
    const waterIntake = aggregateDailyMetric(data.waterIntake || [], 'liters', 'amountLiters');
    const endDate = payload?.meta?.endDate || formatDateInput(new Date());

    if (nutrition.waterIntakeToday?.amount !== undefined && !waterIntake.some(item => item.date === endDate)) {
        waterIntake.push({
            date: endDate,
            amountLiters: Number(nutrition.waterIntakeToday.amount) || 0
        });
    }

    return {
        sourceIdentifier: appUser?.identifier,
        sourceRequestKey,
        raw: payload,
        meta: payload?.meta || {},
        errors: payload?.errors || {},
        userDetails: buildUserDetails(appUser),
        heartRate: (data.heartRate || [])
            .map(item => ({
                timestamp: toISOFromMs(item.start_time_ms || item.timestamp),
                bpm: Number(item.bpm)
            }))
            .filter(item => item.timestamp && Number.isFinite(item.bpm)),
        steps: aggregateDailyMetric(data.steps || [], 'steps', 'steps'),
        waterIntake: waterIntake.sort((a, b) => new Date(a.date) - new Date(b.date)),
        sleep: normalizeSleep(data, payload?.meta || {}),
        sleepSummary: data.sleepSummary || null,
        lastSession: data.lastSession || null,
        capacity: data.capacity || null,
        healthMetrics: normalizeHealthMetrics(data),
        habits: normalizeHabits(payload?.health?.habits ?? nutrition.habits, payload?.health?.habitsCompletions),
        habitsToday: payload?.health?.habitsToday || null,
        habitsCompletions: payload?.health?.habitsCompletions || null,
        habitCompletionHistory: normalizeHabitCompletionHistory(payload?.health?.habitsCompletions),
        meals,
        macros: buildMacroSummary(meals),
        activities: payload?.health?.activities,
        exerciseFavorites: payload?.health?.exerciseFavorites,
        devices: payload?.health?.devices,
        recentSyncEvents: payload?.health?.recentSyncEvents || [],
        calorieGoal: nutrition.calorieGoal,
        waterIntakeToday: nutrition.waterIntakeToday,
        originsUsed: query.meta?.origins_used || {},
        rules: query.meta?.rules || {},
        goals: query.goals || {},
        journals: nutrition.journals || []
    };
};

const createEmptyHealthData = (appUser = {}) => ({
    sourceIdentifier: appUser?.identifier,
    sourceRequestKey: null,
    userDetails: buildUserDetails(appUser || {}),
    heartRate: [],
    steps: [],
    waterIntake: [],
    sleep: [],
    sleepSummary: null,
    lastSession: null,
    capacity: null,
    healthMetrics: normalizeHealthMetrics({}),
    habits: [],
    habitsToday: null,
    habitsCompletions: null,
    habitCompletionHistory: {},
    meals: [],
    macros: { dailySummary: [] },
    activities: null,
    exerciseFavorites: null,
    devices: null,
    recentSyncEvents: [],
    calorieGoal: null,
    waterIntakeToday: null,
    originsUsed: {},
    rules: {},
    goals: {},
    journals: [],
    errors: {}
});


        

function CalendarSquareHeatmap({ habits = [], completionHistory = {}, completionHistoryAvailable = false, selectedDate, setSelectedDate }) {
    const [days, setDays] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const completionDataAvailable = completionHistoryAvailable || habits.some(h => Array.isArray(h.completions));
    
    // Set today as default selected date when component mounts
    useEffect(() => {
        if (!selectedDate) {
            setSelectedDate(new Date());
        }
    }, [selectedDate, setSelectedDate]);
    
    // Generate days for the current month only
    useEffect(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        
        // Last day of the month
        const lastDay = new Date(year, month + 1, 0);
        
        const arr = [];
        const todayKey = getLocalDateKey(new Date());
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const date = new Date(year, month, d);
            const dateKey = getLocalDateKey(date);
            const isFuture = dateKey > todayKey;
            
            let completedCount = 0;
            if (completionDataAvailable) {
                const completedHabitIds = new Set((completionHistory[dateKey] || []).map(item => normalizeHabitId(item.habitId || item.id)));
                if (completedHabitIds.size) {
                    completedCount = completedHabitIds.size;
                } else if (!completionHistoryAvailable) {
                    habits.forEach(h => {
                        if (!isHabitActiveOnDate(h, date)) return;
                        if ((h.completions || []).some(c => completionMatchesDate(c, dateKey))) completedCount++;
                    });
                }
            }

            const definitionHabitCount = isFuture
                ? 0
                : habits.filter(h => isHabitActiveOnDate(h, date)).length;
            const activeHabitCount = Math.max(definitionHabitCount, completedCount);
            
            arr.push({
                date: new Date(date),
                dateStr: dateKey,
                totalHabits: activeHabitCount,
                completedCount
            });
        }
        
        setDays(arr);
    }, [habits, completionHistory, completionHistoryAvailable, currentMonth, completionDataAvailable]);
    
    const handleSelect = (d) => setSelectedDate(d.date);
    
    // Go to previous month
    const prevMonth = () => {
        const prev = new Date(currentMonth);
        prev.setMonth(prev.getMonth() - 1);
        setCurrentMonth(prev);
    };
    
    // Go to next month
    const nextMonth = () => {
        const next = new Date(currentMonth);
        next.setMonth(next.getMonth() + 1);
        setCurrentMonth(next);
    };
    
    // Format month name and year
    const formatMonthYear = (date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    
    return (
        <>
            <div className="APM-month-navigation">
                <button className="APM-month-nav-btn" onClick={prevMonth}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <h3 className="APM-month-title">{formatMonthYear(currentMonth)}</h3>
                <button className="APM-month-nav-btn" onClick={nextMonth}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div className="APM-heatmap-grid">
                {days.map((dObj, idx) => {
                    const ratio = dObj.totalHabits ? dObj.completedCount / dObj.totalHabits : 0;
                    let backgroundColor;
                    
                    if (!completionDataAvailable && dObj.totalHabits > 0) {
                        backgroundColor = '#e1eeff';
                    } else if (ratio === 0) {
                        backgroundColor = '#f7fafc'; // Very light blue/gray for empty days
                    } else if (ratio < 0.25) {
                        backgroundColor = '#e1eeff'; // Very light blue
                    } else if (ratio < 0.5) {
                        backgroundColor = '#bbd6f1'; // Light blue
                    } else if (ratio < 0.75) {
                        backgroundColor = '#6fa9dc'; // Medium blue
                    } else {
                        backgroundColor = '#20558a'; // Full blue matching our theme
                    }
                    const textColor = ratio >= 0.5 ? '#ffffff' : '#4a5568';
                    const isSelected =
                        selectedDate &&
                        getLocalDateKey(dObj.date) === getLocalDateKey(selectedDate);
                    return (
                        <button
                            type="button"
                            key={dObj.dateStr || idx}
                            onClick={() => handleSelect(dObj)}
                            className={`APM-heatmap-day${isSelected ? ' selected' : ''}`}
                            style={{ backgroundColor, color: textColor }}
                            aria-pressed={isSelected}
                            title={
                                completionDataAvailable
                                    ? `Habits completed: ${dObj.completedCount}/${dObj.totalHabits}\n${formatShortDate(dObj.date)}`
                                    : `Tracked habits active: ${dObj.totalHabits}\nCompletion logs unavailable`
                            }
                        >
                            <span>{dObj.date.getDate()}</span>
                        </button>
                    );
                })}
            </div>
        </>
    );
}

function HabitList({ habits = [], completionHistory = {}, completionHistoryAvailable = false, date, loading = false }) {
    if (!date) return <div className="habit-select-prompt">Select a date above</div>;
    
    const dateStr = getLocalDateKey(date);
    const formattedDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const historyItemsForDate = completionHistory[dateStr] || [];
    const habitById = new Map(habits.map(habit => [normalizeHabitId(habit.id), habit]));
    const completedHabitIds = new Set(historyItemsForDate.map(item => normalizeHabitId(item.habitId || item.id)));
    const activeHabits = habits.filter(habit => isHabitActiveOnDate(habit, date));
    const completionDataAvailable = completionHistoryAvailable || activeHabits.some(h => Array.isArray(h.completions));
    
    // Group habits by status
    const completedHabits = completionHistoryAvailable
        ? historyItemsForDate.map((item, index) => {
            const habitId = normalizeHabitId(item.habitId || item.id);
            const definition = habitById.get(habitId);

            return {
                ...definition,
                ...item,
                id: definition?.id || habitId || `history-${dateStr}-${index}`,
                habitId,
                description: definition?.description || item.description || 'Habit',
                goalTime: definition?.goalTime || normalizeGoalTime(item.goalTime || item.goal_time),
                notes: definition?.notes || item.notes
            };
        }).filter(habit => habit.habitId || habit.id)
        : [];
    const missedHabits = [];
    
    activeHabits.forEach(habit => {
        const habitId = normalizeHabitId(habit.id);
        const completed = completedHabitIds.has(habitId) || (
            !completionHistoryAvailable && (habit.completions || []).some(c => completionMatchesDate(c, dateStr))
        );
        if (completed) {
            if (!completionHistoryAvailable) completedHabits.push(habit);
            return;
        }
        missedHabits.push(habit);
    });
    
    // Get habit category icon using FontAwesome icons
    const getHabitIcon = (description = '') => {
        const lowerDesc = description.toLowerCase();
        if (lowerDesc.includes('exercise') || lowerDesc.includes('workout') || lowerDesc.includes('walk')) {
            return faRunning;
        } else if (lowerDesc.includes('water') || lowerDesc.includes('drink')) {
            return faWater;
        }  else if (lowerDesc.includes('read') || lowerDesc.includes('book')) {
            return faBook;
        } else if (lowerDesc.includes('eat') || lowerDesc.includes('meal') || lowerDesc.includes('food')) {
            return faAppleAlt;
        } else if (lowerDesc.includes('sleep') || lowerDesc.includes('bed')) {
            return faBed;
        }
        return faCheckSquare;
    };
    
    const calcCompletion = () => {
        const total = Math.max(activeHabits.length, completedHabits.length);
        if (!total) return { percent: 0, fraction: '0/0' };
        const percent = Math.round((completedHabits.length / total) * 100);
        const fraction = `${completedHabits.length}/${total}`;
        return { percent, fraction };
    };
    
    const completion = calcCompletion();
    
    return (
        <div className="habit-tracker-container">
            <div className="habit-tracker-header">
                <div className="habit-date">{formattedDate}</div>
                {(activeHabits.length > 0 || completedHabits.length > 0) && completionDataAvailable && (
                    <div className="habit-progress">
                        <div className="habit-progress-bar">
                            <div 
                                className="habit-progress-fill" 
                                style={{ width: `${completion.percent}%` }}
                            />
                        </div>
                        <div className="habit-progress-text">{completion.fraction}</div>
                    </div>
                )}
            </div>
            
            <div className="habit-list-container">
                {loading && !habits.length && (
                    <div className="habit-empty loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                )}
                {!loading && !habits.length && <div className="habit-empty">No habits returned for this range</div>}
                {habits.length > 0 && !activeHabits.length && !completedHabits.length && <div className="habit-empty">No habits active on this date</div>}
                
                {!completionDataAvailable && activeHabits.length > 0 && (
                    <div className="habit-section">
                        <div className="habit-section-header">
                            <FontAwesomeIcon icon={faCheckSquare} className="habit-section-icon tracked" />
                            <span>Tracked</span>
                        </div>
                        <div className="habit-items">
                            {activeHabits.map(habit => (
                                <div key={habit.id} className="habit-item tracked">
                                    <div className="habit-item-content">
                                        <div className="habit-icon">
                                            <FontAwesomeIcon icon={getHabitIcon(habit.description)} color="#4a5568" />
                                        </div>
                                        <div className="habit-details">
                                            <div className="habit-description">{habit.description}</div>
                                            {habit.goalTime && (
                                                <div className="habit-time">Goal time: {habit.goalTime?.slice(0,5)}</div>
                                            )}
                                            {habit.notes && (
                                                <div className="habit-time">{habit.notes}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="habit-status">
                                        <div className="habit-status-indicator tracked">
                                            <FontAwesomeIcon icon={faCheckSquare} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {completionDataAvailable && completedHabits.length > 0 && (
                    <div className="habit-section">
                        <div className="habit-section-header">
                            <FontAwesomeIcon icon={faCheckCircle} className="habit-section-icon completed" />
                            <span>Completed</span>
                        </div>
                        <div className="habit-items">
                            {completedHabits.map(habit => (
                                <div key={habit.id} className="habit-item completed">
                                    <div className="habit-item-content">
                                        <div className="habit-icon">
                                            <FontAwesomeIcon icon={getHabitIcon(habit.description)} color="#4a5568" />
                                        </div>
                                        <div className="habit-details">
                                            <div className="habit-description">{habit.description}</div>
                                            {habit.goalTime && (
                                                <div className="habit-time">Goal time: {habit.goalTime?.slice(0,5)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="habit-status">
                                        <div className="habit-status-indicator completed">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {completionDataAvailable && missedHabits.length > 0 && (
                    <div className="habit-section">
                        <div className="habit-section-header">
                            <FontAwesomeIcon icon={faCircleXmark} className="habit-section-icon missed" />
                            <span>Missed</span>
                        </div>
                        <div className="habit-items">
                            {missedHabits.map(habit => (
                                <div key={habit.id} className="habit-item missed">
                                    <div className="habit-item-content">
                                        <div className="habit-icon">
                                            <FontAwesomeIcon icon={getHabitIcon(habit.description)} color="#9c9c9c" />
                                        </div>
                                        <div className="habit-details">
                                            <div className="habit-description">{habit.description}</div>
                                            {habit.goalTime && (
                                                <div className="habit-time">Goal time: {habit.goalTime?.slice(0,5)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="habit-status">
                                        <div className="habit-status-indicator missed">
                                            <FontAwesomeIcon icon={faCircleXmark} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const APMHabitsPanel = React.memo(function APMHabitsPanel({ healthData, loading }) {
    const [calendarDate, setCalendarDate] = useState(() => new Date());
    const habits = healthData?.habits || [];
    const completionHistory = healthData?.habitCompletionHistory || {};
    const completionHistoryAvailable = Boolean(healthData?.habitsCompletions);

    return (
        <>
            <div className="APM-container-final-left-top">
                <CalendarSquareHeatmap
                    habits={habits}
                    completionHistory={completionHistory}
                    completionHistoryAvailable={completionHistoryAvailable}
                    selectedDate={calendarDate}
                    setSelectedDate={setCalendarDate}
                />
            </div>
            <div className="APM-container-final-left-bottom">
                <HabitList
                    habits={habits}
                    completionHistory={completionHistory}
                    completionHistoryAvailable={completionHistoryAvailable}
                    date={calendarDate}
                    loading={loading}
                />
            </div>
        </>
    );
});

function HRDailyBarChart({ data = [], selectedDay, onBarClick }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    const chartData = data.slice(-10);
    const lows = chartData.map(d => d.minBpm ?? 40);
    const highs = chartData.map(d => d.maxBpm ?? 120);
    const dataLow = Math.min(...lows, 40);
    const dataHigh = Math.max(...highs, dataLow + 40);
    const scaleRange = dataHigh - dataLow || 1;
    const yTicks = [
        { value: dataHigh, percent: 100 },
        { value: Math.round((dataHigh + dataLow) / 2), percent: 50 },
        { value: dataLow, percent: 0 }
    ];
    const avgHR = chartData.length
        ? (chartData.reduce((a, b) => a + ((b.minBpm + b.maxBpm) / 2), 0) / chartData.length).toFixed(0)
        : "N/A";

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Heart Rate</h3>
                <div className="APM-chart-avg">{avgHR} <div className="APM-chart-unit">bpm</div></div>
            </div>
            <div className="APM-bar-wrapper">
                <div className="APM-y-axis-area">
                    {yTicks.map((tick, i) => (
                        <div key={i} className="APM-y-tick" style={{ bottom: `${tick.percent}%` }}>
                            <span className="APM-y-label">{tick.value}</span>
                            <span className="APM-y-grid" />
                        </div>
                    ))}
                </div>
                <div className="APM-bar-area">
                    {chartData.map((item, i) => {
                        const min = item.minBpm ?? dataLow;
                        const max = item.maxBpm ?? min;
                        const barHeight = ((max - min) / scaleRange) * 100;
                        const barBottom = ((min - dataLow) / scaleRange) * 100;
                        const sel = selectedDay && item.date === selectedDay;
                        return (
                            <div key={i} className="APM-bar-barcol" style={{ position: 'relative' }}>
                                <div
                                    className={`APM-bar-bar${sel ? " sel" : ""}`}
                                    style={{
                                        position: 'absolute',
                                        left: '25%',
                                        width: '50%',
                                        bottom: `${barBottom}%`,
                                        height: `${barHeight}%`,
                                        backgroundColor: "#90caf9",
                                        borderColor: "#68a4e1"
                                    }}
                                    title={`Min: ${min}, Max: ${max}\n${item.date}`}
                                    onClick={() => onBarClick(item.date)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="APM-bar-x-labels-row">
                {chartData.map((item, i) => (
                    <div key={i} className="APM-bar-x-label">
                        {/* Only show every other label when more than 4 records */}
                        {chartData.length <= 4 || i % 2 === 0 ? formatShortDate(item.date) : ''}
                    </div>
                ))}
            </div>
        </div>
    );
}

function HRHourlyBarChart({ heartRate = [], dayISO }) {
    if (!heartRate.length || !dayISO) return <div className="APM-no-data"></div>;
    const hours = {};
    heartRate.forEach(hr => {
        const ts = new Date(hr.timestamp);
        const ds = ts.toISOString().split('T')[0];
        if (ds !== dayISO) return;
        const h = ts.getHours();
        if (!hours[h]) hours[h] = { min: hr.bpm, max: hr.bpm };
        else {
            hours[h].min = Math.min(hours[h].min, hr.bpm);
            hours[h].max = Math.max(hours[h].max, hr.bpm);
        }
    });
    const hourList = Object.keys(hours).map(h => ({ hour: Number(h), ...hours[h] })).sort((a, b) => a.hour - b.hour);
    if (!hourList.length) return <div className="APM-no-data"></div>;

    const minBpm = Math.min(...hourList.map(h => h.min));
    const maxBpm = Math.max(...hourList.map(h => h.max), minBpm + 20);
    const range = maxBpm - minBpm || 1;
    const avgDayHR = hourList.length
        ? (hourList.reduce((a, b) => a + ((b.min + b.max) / 2), 0) / hourList.length).toFixed(0)
        : "N/A";
    const yTicks = [
        { value: maxBpm, percent: 100 },
        { value: Math.round((maxBpm + minBpm) / 2), percent: 50 },
        { value: minBpm, percent: 0 }
    ];

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Hourly Heart Rate</h3>
                <div className="APM-chart-avg">{avgDayHR} <div className="APM-chart-unit">bpm</div></div>
            </div>
            <div className="APM-bar-wrapper">
                <div className="APM-y-axis-area">
                    {yTicks.map((tick, i) => (
                        <div key={i} className="APM-y-tick" style={{ bottom: `${tick.percent}%` }}>
                            <span className="APM-y-label">{tick.value}</span>
                            <span className="APM-y-grid" />
                        </div>
                    ))}
                </div>
                <div className="APM-bar-area">
                    {hourList.map(hrhr => {
                        const { hour, min, max } = hrhr;
                        const barHeight = ((max - min) / range) * 100;
                        const barBottom = ((min - minBpm) / range) * 100;
                        return (
                            <div key={hour} className="APM-bar-barcol" style={{ position: 'relative' }}>
                                <div
                                    className="APM-bar-bar"
                                    style={{
                                        position: 'absolute',
                                        left: '25%',
                                        width: '50%',
                                        bottom: `${barBottom}%`,
                                        height: `${barHeight}%`,
                                        backgroundColor: "#90caf9",
                                        borderColor: "#68a4e1"
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="APM-bar-x-labels-row">
                {hourList.map((hrhr, i) => (
                    <div key={hrhr.hour} className="APM-bar-x-label">
                        {/* Only show every other label when more than 4 records */}
                        {hourList.length <= 4 || i % 2 === 0 ? String(hrhr.hour).padStart(2,'0') : ''}
                    </div>
                ))}
            </div>
        </div>
    );
}

function StepsLineChart({ data = [] }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    
    const chartData = data.map(item => ({
        date: formatShortDate(item.date),
        steps: item.steps,
        fullDate: item.date
    }));
    
    const avgSteps = data.length ? (data.reduce((a, b) => a + b.steps, 0) / data.length).toFixed(0) : "N/A";
    
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${payload[0].value.toLocaleString()} steps`}</p>
                </div>
            );
        }
        return null;
    };
    
    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Daily Steps</h3>
                <div className="APM-chart-avg">{avgSteps} <span className="APM-chart-unit">steps</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="stepsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => value.toLocaleString()}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="steps" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#stepsGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function ValueBarChart({ data = [], valueKey, unit, title }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    const avgValue = data.length ? (data.reduce((a, b) => a + b[valueKey], 0) / data.length).toFixed(1) : "N/A";
    
    // Custom tooltip for water intake that looks like the steps tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${payload[0].value.toLocaleString()} ${unit || ''}`}</p>
                </div>
            );
        }
        return null;
    };
    
    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>{title}</h3>
                <div className="APM-chart-avg">{avgValue} <span className="APM-chart-unit">{unit}</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data.map(item => ({
                            date: formatShortDate(item.date),
                            value: item[valueKey],
                            fullDate: item.date
                        }))}
                        margin={{ top: 10, right: 10, left: -35, bottom: -10 }}
                    >
                        <defs>
                            <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => value.toLocaleString()}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#valueGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function SleepBarChart({ sleep = [] }) {
    const last7 = getLatestNRecordsAsc(sleep, 'date', 7);
    
    // Convert sleep data to a format suitable for AreaChart
    const chartData = last7.map(s => {
        const duration = s.durationMinutes
            ? {
                totalHours: s.durationMinutes / 60,
                text: `${Math.floor(s.durationMinutes / 60)}h ${Math.round(s.durationMinutes % 60)}m`
            }
            : calculateDuration(s.sleepTime, s.wakeTime);
        return {
            date: formatShortDate(s.date),
            value: duration.totalHours,
            fullDate: s.date,
            sleepTime: s.sleepTime,
            wakeTime: s.wakeTime,
            durationText: duration.text
        };
    });
    
    // Early return for no data - EXACT match to ValueBarChart approach
    if (!chartData.length) return <div className="APM-no-data"></div>;
    
    const avgDuration = (chartData.reduce((a, b) => a + b.value, 0) / chartData.length).toFixed(1);
    
    // Custom tooltip for sleep data
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${data.value.toFixed(1)} hours`}</p>
                    {data.sleepTime && data.wakeTime && (
                        <p className="tooltip-time">{`${formatTime(data.sleepTime)} → ${formatTime(data.wakeTime)}`}</p>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Sleep Duration</h3>
                <div className="APM-chart-avg">{avgDuration} <div className="APM-chart-unit">h</div></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -35, bottom: -10 }}
                    >
                        <defs>
                            <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => `${value}h`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#sleepGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const isRealNumber = (value) => Number.isFinite(Number(value));
const toNumber = (value) => Number(value);

const formatMetricNumber = (value, digits = 0) => {
    if (!isRealNumber(value)) return 'N/A';
    const number = toNumber(value);
    return number.toLocaleString(undefined, {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits > 0 ? 1 : 0
    });
};

const getLatestRecord = (items = []) => {
    if (!items.length) return null;
    return items.reduce((latest, item) => {
        const itemTime = new Date(item.timestamp || item.date || 0).getTime();
        const latestTime = new Date(latest?.timestamp || latest?.date || 0).getTime();
        return itemTime >= latestTime ? item : latest;
    }, items[0]);
};

function APMMetricCard({ title, value, unit, detail, footer, children, wide = false }) {
    return (
        <div className={`APM-data-card ${wide ? 'wide' : ''}`}>
            <div className="APM-data-card-title">{title}</div>
            <div className="APM-data-card-main">
                <span>{value ?? 'N/A'}</span>
                {unit && <em>{unit}</em>}
            </div>
            {detail && <div className="APM-data-card-detail">{detail}</div>}
            {footer && <div className="APM-data-card-footer">{footer}</div>}
            {children}
        </div>
    );
}

function APMDataRow({ title, children }) {
    return (
        <section className="APM-data-row">
            <div className="APM-data-row-title">{title}</div>
            <div className="APM-data-card-grid">{children}</div>
        </section>
    );
}

function APMLoadingOverlay({ visible }) {
    if (!visible) return null;

    return (
        <div className="APM-panel-loading-overlay" aria-live="polite">
            <FontAwesomeIcon icon={faSpinner} spin className="APM-panel-loading-spinner" />
        </div>
    );
}

const getPointTime = (item = {}) =>
    item.timestamp || item.date || toISOFromMs(item.date_ms || item.start_time_ms || item.started_at_ms || item.created_at);

const formatSeriesLabel = (timeValue) => {
    if (!timeValue) return '';
    const date = new Date(timeValue);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatSeriesTime = (timeValue) => {
    if (!timeValue) return 'N/A';
    const date = new Date(timeValue);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getNutrientAmount = (day = {}, name) => {
    const nutrient = (day.nutrients || []).find(item => item.nutrient === name);
    return Number(nutrient?.amount) || 0;
};

function APMChartSection({ title, children, className = '' }) {
    const chartCount = React.Children.toArray(children).filter(Boolean).length || 1;
    const chartCountClass = `chart-count-${chartCount}`;

    return (
        <section className={`APM-chart-section ${className}`.trim()}>
            <div className="APM-chart-section-title">{title}</div>
            <div className={`APM-chart-row-grid ${chartCountClass}`}>{children}</div>
        </section>
    );
}

function APMHeartFillChart({ stepsData = [] }) {
    const latestSteps = stepsData.length ? stepsData[stepsData.length - 1]?.steps : null;

    return (
        <div className="APM-chart-container APM-heart-fill-chart">
            <div className="APM-chart-header">
                <h3>Step Goal</h3>
                <div className="APM-chart-avg">
                    {latestSteps !== null ? latestSteps.toLocaleString() : 0}
                    <span className="APM-chart-unit">steps</span>
                </div>
            </div>
            <div className="APM-chart-content">
                <HeartFillComponent stepsData={stepsData} />
            </div>
        </div>
    );
}

function APMEmptyChart({ title, unit }) {
    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>{title}</h3>
                <div className="APM-chart-avg">0 {unit && <span className="APM-chart-unit">{unit}</span>}</div>
            </div>
            <div className="APM-chart-empty-content">No records in range</div>
        </div>
    );
}

function APMSeriesAreaChart({ title, data = [], valueKey = 'value', unit = '', digits = 0, color = '#90caf9', maxPoints = 14, variant = 'area' }) {
    const chartData = data
        .map(item => {
            const time = getPointTime(item);
            const value = Number(item[valueKey]);
            return {
                time,
                label: formatSeriesLabel(time),
                value,
                fullLabel: formatSeriesTime(time)
            };
        })
        .filter(item => item.time && Number.isFinite(item.value))
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .slice(-maxPoints);

    if (!chartData.length) return <APMEmptyChart title={title} unit={unit} />;

    const avg = chartData.reduce((sum, item) => sum + item.value, 0) / chartData.length;
    const gradientId = `apm-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-gradient`;

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const point = payload[0].payload;
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{point.fullLabel}</p>
                    <p className="tooltip-steps">{`${formatMetricNumber(point.value, digits)} ${unit}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>{title}</h3>
                <div className="APM-chart-avg">{formatMetricNumber(avg, digits)} <span className="APM-chart-unit">{unit}</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    {variant === 'line' ? (
                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={color}
                                strokeWidth={2.2}
                                dot={{ r: 2, fill: color, strokeWidth: 0 }}
                                activeDot={{ r: 5, strokeWidth: 0, fill: '#174573' }}
                            />
                        </LineChart>
                    ) : (
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -10 }}>
                            <defs>
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={color}
                                strokeWidth={2}
                                fill={`url(#${gradientId})`}
                                activeDot={{ r: 5, strokeWidth: 0, fill: '#174573' }}
                            />
                        </AreaChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function APMBloodPressureChart({ data = [] }) {
    const chartData = data
        .map(item => {
            const time = getPointTime(item);
            return {
                time,
                label: formatSeriesLabel(time),
                fullLabel: formatSeriesTime(time),
                systolic: Number(item.systolic),
                diastolic: Number(item.diastolic)
            };
        })
        .filter(item => item.time && (Number.isFinite(item.systolic) || Number.isFinite(item.diastolic)))
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .slice(-14);

    if (!chartData.length) return <APMEmptyChart title="Blood Pressure" unit="mmHg" />;

    const latest = chartData[chartData.length - 1];

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const point = payload[0].payload;
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{point.fullLabel}</p>
                    <p className="tooltip-steps">{`${formatMetricNumber(point.systolic)}/${formatMetricNumber(point.diastolic)} mmHg`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Blood Pressure</h3>
                <div className="APM-chart-avg">{formatMetricNumber(latest.systolic)}/{formatMetricNumber(latest.diastolic)} <span className="APM-chart-unit">mmHg</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: -10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="systolic" stroke="#4D96FF" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="diastolic" stroke="#74C69D" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function PlatformDataRows({
    healthData,
    dailyHRList,
    chartDay,
    onChartDayChange,
    stepsForHeartComponent,
    stepsLast10,
    waterLast10
}) {
    const metrics = healthData?.healthMetrics || {};
    const sleepSummary = healthData?.sleepSummary || {};
    const devices = healthData?.devices?.devices || [];
    const primaryDevice = devices.find(device => Number(device.preference) === 1) || devices[0];
    const syncEvents = healthData?.recentSyncEvents || [];
    const latestSync = syncEvents[0] || getLatestRecord(syncEvents);
    const favorites = healthData?.exerciseFavorites?.favorites || [];
    const meals = healthData?.meals || [];
    const sleepSeries = sleepSummary.series || {};
    const macroDays = healthData?.macros?.dailySummary || [];
    const macroSeries = macroDays.map(day => ({
        date: day.date,
        calories: getNutrientAmount(day, 'Energy'),
        protein: getNutrientAmount(day, 'Protein'),
        carbs: getNutrientAmount(day, 'Carbohydrate, by difference'),
        fat: getNutrientAmount(day, 'Total lipid (fat)')
    }));
    const mealSeries = meals.reduce((acc, meal) => {
        const date = meal.date || meal.time?.split('T')[0];
        if (!date) return acc;
        acc[date] = acc[date] || { date, meals: 0, foods: 0 };
        acc[date].meals += 1;
        acc[date].foods += meal.items?.length || 0;
        return acc;
    }, {});
    const sleepScoreSeries = (sleepSeries.score || []).map(item => ({
        timestamp: toISOFromMs(item.date_ms),
        value: Number(item.value)
    }));
    const sleepEfficiencySeries = (sleepSeries.efficiency || []).map(item => ({
        timestamp: toISOFromMs(item.date_ms),
        value: Number(item.value)
    }));
    const sleepRestorativeSeries = (sleepSeries.restorative || []).map(item => ({
        timestamp: toISOFromMs(item.date_ms),
        value: (Number(item.deep) || 0) + (Number(item.rem) || 0)
    }));
    return (
        <>
            <APMChartSection title="Daily Activity" className="daily-activity-row">
                <APMHeartFillChart stepsData={stepsForHeartComponent || []} />
                <StepsLineChart data={stepsLast10 || []} />
            </APMChartSection>

            <APMChartSection title="Cardio Signals">
                <HRDailyBarChart
                    data={dailyHRList}
                    selectedDay={chartDay}
                    onBarClick={onChartDayChange}
                />
                <HRHourlyBarChart
                    heartRate={healthData?.heartRate || []}
                    dayISO={chartDay}
                />
                <APMSeriesAreaChart
                    title="Resting HR"
                    data={metrics.restingHeartRate || []}
                    valueKey="bpm"
                    unit="bpm"
                    color="#4D96FF"
                    variant="line"
                />
                <APMSeriesAreaChart
                    title="HRV"
                    data={metrics.hrv || []}
                    valueKey="hrvMs"
                    unit="ms"
                    color="#74C69D"
                    variant="line"
                />
            </APMChartSection>

            <APMChartSection title="Vitals">
                <APMSeriesAreaChart
                    title="Blood Oxygen"
                    data={metrics.bloodOxygen || []}
                    valueKey="percent"
                    unit="%"
                    digits={1}
                    color="#69a9dd"
                    variant="line"
                />
                <APMBloodPressureChart data={metrics.bloodPressure || []} />
                <APMSeriesAreaChart
                    title="Respiratory Rate"
                    data={metrics.respiratoryRate || []}
                    valueKey="breathsPerMin"
                    unit="br/min"
                    digits={1}
                    color="#4D96FF"
                    variant="line"
                />
            </APMChartSection>

            <APMChartSection title="Sleep">
                <SleepBarChart sleep={healthData?.sleep || []} />
                <APMSeriesAreaChart
                    title="Sleep Score"
                    data={sleepScoreSeries}
                    valueKey="value"
                    unit="score"
                    color="#4D96FF"
                />
                <APMSeriesAreaChart
                    title="Efficiency"
                    data={sleepEfficiencySeries}
                    valueKey="value"
                    unit="%"
                    color="#74C69D"
                />
                <APMSeriesAreaChart
                    title="Restorative"
                    data={sleepRestorativeSeries}
                    valueKey="value"
                    unit="min"
                    color="#B57EDC"
                />
            </APMChartSection>

            <APMChartSection title="Movement">
                <APMSeriesAreaChart
                    title="Distance"
                    data={(metrics.distance || []).map(item => ({ ...item, km: (Number(item.meters) || 0) / 1000 }))}
                    valueKey="km"
                    unit="km"
                    digits={2}
                    color="#90caf9"
                />
                <APMSeriesAreaChart
                    title="Active Calories"
                    data={metrics.calories || []}
                    valueKey="kcal"
                    unit="kcal"
                    color="#ffb74d"
                />
            </APMChartSection>

            <APMChartSection title="Nutrition And Hydration">
                <ValueBarChart
                    data={waterLast10 || []}
                    valueKey="amountLiters"
                    unit="L"
                    title="Water Intake"
                />
                <APMSeriesAreaChart
                    title="Daily Calories"
                    data={macroSeries}
                    valueKey="calories"
                    unit="kcal"
                    color="#90caf9"
                />
                <APMSeriesAreaChart
                    title="Protein"
                    data={macroSeries}
                    valueKey="protein"
                    unit="g"
                    color="#74C69D"
                />
                <APMSeriesAreaChart
                    title="Carbs"
                    data={macroSeries}
                    valueKey="carbs"
                    unit="g"
                    color="#ffb74d"
                />
            </APMChartSection>

            <APMChartSection title="Meal Logging">
                <APMSeriesAreaChart
                    title="Meals Logged"
                    data={Object.values(mealSeries)}
                    valueKey="meals"
                    unit="meals"
                    color="#B57EDC"
                />
            </APMChartSection>

            <APMDataRow title="Device Sync">
                <APMMetricCard
                    title="Devices"
                    value={devices.length}
                    unit="linked"
                    detail={primaryDevice?.name || primaryDevice?.mac || 'No paired device'}
                    footer={primaryDevice?.status || primaryDevice?.origin}
                />
                <APMMetricCard
                    title="Sync Events"
                    value={syncEvents.length}
                    unit="events"
                    detail={latestSync?.status || 'No sync events'}
                    footer={latestSync?.duration_ms ? `${latestSync.duration_ms} ms` : null}
                />
                <APMMetricCard
                    title="Favorites"
                    value={favorites.length}
                    unit="types"
                    detail={favorites.slice(0, 3).join(', ') || 'No favorites'}
                />
            </APMDataRow>
        </>
    );
}


function AppProfileModal({ appUser }) {
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartDay, setChartDay] = useState(null);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const initialRange = React.useMemo(() => getPresetDateRange('7d'), []);
    const [rangePreset, setRangePreset] = useState('7d');
    const [rangeStartDate, setRangeStartDate] = useState(initialRange.startDate);
    const [rangeEndDate, setRangeEndDate] = useState(initialRange.endDate);
    const appUserIdentifier = appUser?.identifier;
    const emptyDisplayHealthData = React.useMemo(() => createEmptyHealthData(appUser), [appUser]);
    const rangeQuery = React.useMemo(
        () => getRangeQuery(rangePreset, rangeStartDate, rangeEndDate),
        [rangePreset, rangeStartDate, rangeEndDate]
    );
    const activeRequestKey = appUserIdentifier && rangeQuery.startTime && rangeQuery.endTime
        ? `${appUserIdentifier}|${rangeQuery.startTime}|${rangeQuery.endTime}`
        : null;
    const rangeIsMissing = rangePreset !== '24h' && (!rangeStartDate || !rangeEndDate);
    const rangeIsInvalid = rangePreset !== '24h' && new Date(rangeStartDate) > new Date(rangeEndDate);
    const healthDataMatchesRequest = healthData?.sourceRequestKey === activeRequestKey;
    const displayHealthData = healthDataMatchesRequest ? healthData : emptyDisplayHealthData;
    const showDataLoading = loading || (
        Boolean(activeRequestKey) &&
        !healthDataMatchesRequest &&
        !error &&
        !rangeIsMissing &&
        !rangeIsInvalid
    );

    const handlePresetChange = (presetKey) => {
        setRangePreset(presetKey);
        if (presetKey !== '24h') {
            const nextRange = getPresetDateRange(presetKey);
            setRangeStartDate(nextRange.startDate);
            setRangeEndDate(nextRange.endDate);
        }
    };

    const handleDateChange = (field, value) => {
        setRangePreset('custom');
        if (field === 'start') setRangeStartDate(value);
        else setRangeEndDate(value);
    };

    const stepsForHeartComponent = React.useMemo(() => {
         const rawSteps = displayHealthData?.steps || [];
         return getLatestNRecordsAsc(rawSteps, 'date', 30); // Get last 30 days ASC
    }, [displayHealthData?.steps]);

    const fetchHealthData = React.useCallback(async (identifier, startTime, endTime, signal) => {
        const requestKey = `${identifier}|${startTime}|${endTime}`;
        setLoading(true);
        setError(null);
        setChartDay(null);
        setHealthData({ ...emptyDisplayHealthData, sourceRequestKey: requestKey });

        try {
            const params = new URLSearchParams({
                userId: identifier,
                startTime,
                endTime
            });
            const response = await fetch(`${TEMP_PLATFORM_VIEW_URL}?${params.toString()}`, { signal });
            if (!response.ok) throw new Error(`Network error (${response.status})`);
            const data = await response.json();
            if (!data || typeof data !== 'object') throw new Error('Invalid data format');
            setHealthData(normalizeTempPlatformViewResponse(data, appUser, requestKey));
        } catch (error) {
            if (error.name === 'AbortError') return;
            setError(`Load failed: ${error.message}`);
            setHealthData({ ...emptyDisplayHealthData, sourceRequestKey: requestKey });
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [appUser, emptyDisplayHealthData]);

    useEffect(() => {
        if (!appUser) {
            setHealthData(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        if (!appUserIdentifier) {
            setHealthData(createEmptyHealthData(appUser));
            setError('Missing app user identifier.');
            setLoading(false);
            return undefined;
        }

        if (rangeIsMissing || !rangeQuery.startTime || !rangeQuery.endTime) {
            setError('Select a valid date range.');
            setLoading(false);
            return undefined;
        }

        if (rangeIsInvalid) {
            setError('Start date must be before end date.');
            setLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        fetchHealthData(appUserIdentifier, rangeQuery.startTime, rangeQuery.endTime, controller.signal);
        return () => controller.abort();
    }, [appUser, appUserIdentifier, rangeQuery.startTime, rangeQuery.endTime, rangeIsMissing, rangeIsInvalid, fetchHealthData]);

    const [lottieData, setLottieData] = useState(null);


    // Load Lottie animation on component mount
    useEffect(() => {
        fetch('https://lottie.host/e225fcd9-4a02-4001-a8f5-b67e21e1bc42/nAt6uKG3C5.json')
            .then(response => response.json())
            .then(data => setLottieData(data))
            .catch(error => console.error('Error loading Lottie animation:', error));
    }, []);

    const dailyHRList = React.useMemo(() => {
        if (!displayHealthData?.heartRate?.length) return [];
        const daily = {};
        displayHealthData.heartRate.forEach(hr => {
            const d = new Date(hr.timestamp).toISOString().split('T')[0];
            if (!daily[d]) daily[d] = { date: d, minBpm: hr.bpm, maxBpm: hr.bpm };
            else {
                daily[d].minBpm = Math.min(daily[d].minBpm, hr.bpm);
                daily[d].maxBpm = Math.max(daily[d].maxBpm, hr.bpm);
            }
        });
        return Object.values(daily).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-10);
    }, [displayHealthData]);

    const stepsLast10 = getLatestNRecordsAsc(displayHealthData?.steps || [], 'date', 10);
    const waterLast10 = getLatestNRecordsAsc(displayHealthData?.waterIntake || [], 'date', 10);

    useEffect(() => {
        if (dailyHRList.length && (!chartDay || !dailyHRList.some(item => item.date === chartDay))) {
            setChartDay(dailyHRList[dailyHRList.length - 1]?.date);
        }
    }, [dailyHRList, chartDay]);

    if (!appUser) return <div className="APM-no-user">Select app user</div>;

    const partialErrorCount = Object.keys(displayHealthData.errors || {}).length;

    return (
        <div className="APM-container-final">
            <PatientLongevityAI 
                isOpen={aiPanelOpen} 
                onClose={() => setAiPanelOpen(false)} 
                userData={appUser}
            />
            <div className="APM-container-final-left">
            <div className="APM-container-final-left-bottom-highest">
                <button className="ai-analysis-button" onClick={() => setAiPanelOpen(true)}>
                    <Lottie
                        loop
                        animationData={lottieData}
                        play
                        className="APM-container-final-left-bottom-highest-lottie"
                    />
                    <span className="ai-analysis-text">Longevity AI</span>
                    <FontAwesomeIcon icon={faChevronRight} color="grey" size="xs" />
                </button>
            </div>
                <APMHabitsPanel healthData={displayHealthData} loading={showDataLoading} />
             
            </div>
            <div className="APM-container-final-right">
            <div className="APM-container-final-right-wrap">
                <div className="APM-range-toolbar">
                    <div className="APM-range-presets">
                        {RANGE_PRESETS.map(preset => (
                            <button
                                key={preset.key}
                                type="button"
                                className={`APM-range-preset ${rangePreset === preset.key ? 'active' : ''}`}
                                onClick={() => handlePresetChange(preset.key)}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <div className="APM-range-dates">
                        <input
                            type="date"
                            value={rangeStartDate}
                            onChange={(event) => handleDateChange('start', event.target.value)}
                        />
                        <span>to</span>
                        <input
                            type="date"
                            value={rangeEndDate}
                            onChange={(event) => handleDateChange('end', event.target.value)}
                        />
                    </div>
                    <div className="APM-range-status">
                        {showDataLoading && (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Loading</span>
                            </>
                        )}
                        {!showDataLoading && partialErrorCount > 0 && (
                            <>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span>{partialErrorCount} partial error{partialErrorCount === 1 ? '' : 's'}</span>
                            </>
                        )}
                    </div>
                </div>
                {error && <div className="APM-inline-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>}
                <div className={`APM-health-data-shell ${showDataLoading ? 'loading' : ''}`}>
                    <APMLoadingOverlay visible={showDataLoading} />
                    <AppUserDetails userDetails={displayHealthData} />
                    <PlatformDataRows
                        healthData={displayHealthData}
                        dailyHRList={dailyHRList}
                        chartDay={chartDay}
                        onChartDayChange={(daystr) => setChartDay(daystr)}
                        stepsForHeartComponent={stepsForHeartComponent}
                        stepsLast10={stepsLast10}
                        waterLast10={waterLast10}
                    />
                    <div className="APM-container-final-right-bottom">
                        <NutritionDailyView healthData={displayHealthData} />
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

export default AppProfileModal;
